import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';
import { UnifiedOrderBadgeComponent } from 'app/shared/components/unified-order-badge/unified-order-badge.component';
import { OrderAttributes } from '@albatrosdeveloper/ave-models-package';
import OrderSourceTransactionAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order-source-transaction/order-source-transaction.entity';

/**
 * Document Number Cell Renderer Component
 *
 * Complex cell renderer that displays:
 * 1. Document serial with navigation (using DocumentNavigationComponent)
 * 2. External keys code below the serial
 * 3. Unified order badge (using UnifiedOrderBadgeComponent)
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'serial',
 *   cellRenderer: DocumentNumberCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-document-number-cell-renderer',
  standalone: true,
  imports: [
    CommonModule,
    DocumentNavigationComponent,
    UnifiedOrderBadgeComponent,
  ],
  template: `
    <div class="flex flex-row items-center gap-1 !text-sm" style="min-width: 55px">
      <p class="table-text-m">
        <span class="table-text-code">
          <b>
            <app-document-navigation
              [documentType]="documentType"
              [documentSerial]="serial"
              [sourceTransactions]="sourceTransactions"
              [displayText]="serial || '-'"
              [showIcon]="true">
            </app-document-navigation>
          </b>
        </span><br />
        {{ externalKeysCode }}
      </p>

      <app-unified-order-badge [order]="orderData"></app-unified-order-badge>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentNumberCellRendererComponent implements CellRenderer<OrderAttributes, string> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<OrderAttributes, string>;

  /**
   * Document serial number
   */
  get serial(): string | undefined {
    return this.params?.data?.serial;
  }

  /**
   * Document type code (e.g., 'DT002', 'DT003')
   */
  get documentType(): string {
    return this.params?.data?.type?.code || '';
  }

  /**
   * External keys code
   */
  get externalKeysCode(): string {
    return this.params?.data?.externalKeys?.code || '';
  }

  /**
   * Is this a unified order?
   */
  get isUnified(): boolean {
    return this.params?.data?.isUnified ?? false;
  }

  /**
   * Source transactions (for unified orders)
   */
  get sourceTransactions(): OrderSourceTransactionAttributes[] {
    return this.params?.data?.sourceTransactions || [];
  }

  /**
   * Order data for the unified order badge
   */
  get orderData(): Partial<OrderAttributes> {
    return {
      serial: this.serial,
      sourceTransactions: this.sourceTransactions,
      operationType: this.params?.data?.operationType,
      isUnified: this.isUnified
    };
  }
}
